import { ursaColors } from "@smartsheet/design-tokens";
import { themed } from "@smartsheet/sds-theme-internal";
import * as colors from "../../../constants/colors";
import { mapButtonAppearance } from "./mapButtonAppearance";

// In app-core (oldestar theme), Buttons have an opacity of 0.3 applied to them when they are
// disabled. We've used gpick to approximate this for the oldestar theme w/o an opacity filter.
export var buttonBackgroundColors = themed({
  primary: {
    rest: {
      "lodestar-light": colors.buttonPrimary,
      oldestar: "#0073ec",
      "ursa-light": colors.buttonPrimary
    },
    focus: {
      "lodestar-light": colors.buttonPrimaryFocus,
      oldestar: "#0073ec",
      "ursa-light": colors.buttonPrimaryFocus
    },
    hover: {
      "lodestar-light": colors.buttonPrimaryHover,
      oldestar: "#005cbc",
      "ursa-light": colors.buttonPrimaryHover
    },
    active: {
      "lodestar-light": colors.buttonPrimaryPressed,
      oldestar: "#0050a5",
      "ursa-light": colors.buttonPrimaryPressed
    },
    disabled: {
      "lodestar-light": colors.buttonPrimaryDisabled,
      oldestar: "#a9ccf0",
      "ursa-light": colors.buttonPrimaryDisabled
    }
  },
  secondary: {
    rest: {
      "lodestar-light": colors.buttonDefault,
      oldestar: "#888888",
      "ursa-light": colors.buttonDefault
    },
    focus: {
      "lodestar-light": colors.buttonDefaultFocus,
      oldestar: "#888888",
      "ursa-light": colors.buttonDefaultFocus
    },
    hover: {
      "lodestar-light": colors.buttonDefaultHover,
      oldestar: "#7d7d7d",
      "ursa-light": colors.buttonDefaultHover
    },
    active: {
      "lodestar-light": colors.buttonDefaultPressed,
      oldestar: "#777777",
      "ursa-light": colors.buttonDefaultPressed
    },
    disabled: {
      "lodestar-light": colors.buttonDefaultDisabled,
      oldestar: "#d2d2d2",
      "ursa-light": colors.buttonDefaultDisabled
    }
  },
  destructive: {
    rest: colors.buttonDestructive,
    focus: colors.buttonDestructiveFocus,
    hover: colors.buttonDestructiveHover,
    active: colors.buttonDestructivePressed,
    disabled: colors.buttonDestructiveDisabled
  },
  "secondary-destructive": {
    rest: colors.buttonDefault,
    focus: colors.buttonDefaultFocus,
    hover: colors.buttonDefaultHover,
    active: colors.buttonDefaultPressed,
    disabled: colors.buttonDefaultDisabled
  },
  share: {
    rest: colors.ctaShare,
    focus: colors.ctaShareFocus,
    hover: colors.ctaShareHover,
    active: colors.ctaShareActive,
    disabled: colors.ctaShareDisabled
  },
  borderless: {
    rest: "transparent",
    hover: colors.buttonDefault,
    active: colors.buttonDefaultHover,
    focus: colors.buttonDefault,
    disabled: "transparent"
  },
  link: {
    rest: "transparent",
    hover: "transparent",
    active: "transparent",
    focus: "transparent",
    disabled: "transparent"
  },
  "primary-filled": {
    rest: ursaColors.blue500,
    hover: ursaColors.blue600,
    active: ursaColors.blue700,
    focus: ursaColors.blue500,
    disabled: ursaColors.blue300
  },
  "primary-empty": {
    rest: "transparent",
    hover: ursaColors.blue50,
    active: ursaColors.blue75,
    focus: ursaColors.neutral00,
    disabled: "transparent"
  },
  "secondary-filled": {
    rest: ursaColors.neutral50,
    hover: ursaColors.neutral75,
    active: ursaColors.neutral100,
    focus: ursaColors.neutral50,
    disabled: ursaColors.neutral50
  },
  "secondary-outlined": {
    rest: ursaColors.neutral00,
    hover: ursaColors.neutral60,
    active: ursaColors.neutral75,
    focus: ursaColors.neutral00,
    disabled: ursaColors.neutral50
  },
  "secondary-empty": {
    rest: "transparent",
    hover: ursaColors.neutral50,
    active: ursaColors.neutral75,
    focus: ursaColors.neutral00,
    disabled: "transparent"
  },
  "canvas-filled": {
    rest: ursaColors.neutral35,
    hover: ursaColors.slate60,
    active: ursaColors.slate75,
    focus: ursaColors.slate50,
    disabled: ursaColors.neutral35
  },
  "canvas-empty": {
    rest: ursaColors.slate25,
    hover: ursaColors.slate50,
    active: ursaColors.slate75,
    focus: "transparent",
    disabled: ursaColors.slate60
  },
  "destructive-filled": {
    rest: ursaColors.red500,
    hover: ursaColors.red600,
    active: ursaColors.red700,
    focus: ursaColors.red500,
    disabled: ursaColors.red300
  },
  "destructive-empty": {
    rest: "transparent",
    hover: ursaColors.red50,
    active: ursaColors.red75,
    focus: "transparent",
    disabled: "transparent"
  }
});

// For mapping state to the color object above
var mapStateToBackgroundString = function mapStateToBackgroundString(_ref) {
  var state = _ref.state,
    focusMode = _ref.focusMode;
  // Disabled should always win
  if (state.isDisabled) {
    return "disabled";
  }
  // Active should win over hover
  if (state.isActive) {
    return "active";
  }
  if (state.isFocus && focusMode === "keyboard") {
    return "focus";
  }
  if (state.isHover) {
    return "hover";
  }
  return "rest";
};
export var getButtonBackgroundColor = function getButtonBackgroundColor(props) {
  var resolvedState = mapStateToBackgroundString(props);
  return buttonBackgroundColors[mapButtonAppearance(props.appearance, props.themeMode)][resolvedState](props.themeMode);
};

// For mapping state to the color object above
var mapStateToBackgroundVar = function mapStateToBackgroundVar(_ref2) {
  var state = _ref2.state,
    focusMode = _ref2.focusMode;
  // Disabled should always win
  if (state.isDisabled) {
    return "--smar--sds--button--color--background--disabled";
  }
  // Active should win over hover
  if (state.isActive) {
    return "--smar--sds--button--color--background--active";
  }
  if (state.isFocus && focusMode === "keyboard") {
    return "--smar--sds--button--color--background--focus";
  }
  if (state.isHover) {
    return "--smar--sds--button--color--background--hover";
  }
  return "--smar--sds--button--color--background--rest";
};
export var getButtonBackgroundColorVar = function getButtonBackgroundColorVar(props) {
  return "".concat(mapStateToBackgroundVar(props));
};
//# sourceMappingURL=getButtonBackgroundColor.js.map